import type { Address } from "viem";

/**
 * El puente entre cadenas. Dos proveedores, en este orden:
 *
 *  1. RELAY. Es el que cobra la comision de Keellabs (0,10%) via `appFees`, y suele ser mas rapido
 *     (segundos, no minutos).
 *  2. LI.FI, de respaldo. Va por `/lifi/` — un proxy de nginx que le pone la clave de API del
 *     lado del servidor. Llamar a li.quest directamente desde el navegador dejaria esa clave a la
 *     vista de cualquiera que abra las herramientas de desarrollo.
 *
 * Si Relay no tiene ruta se intenta LI.FI y se conservan LOS DOS motivos para enseñarlos juntos:
 * cuando falla, casi siempre es que el importe esta por debajo del minimo del puente, y decirlo
 * ahorra al usuario probar a ciegas.
 */

export const LIFI = "/lifi/v1";
export const RELAY = "https://api.relay.link";

/** 0,10% por operacion, al treasury de PRODUCCIÓN (como siempre en keellabs.app). Solo afecta al
 *  barrido de polvo, que sigue por Relay; el puente/​swap usa el widget de LI.FI (comisión en su panel). */
export const COMISION_BPS = 10;
export const WALLET_COMISION = "0xf5E0D81FadF99484301B9e5644850aC8eD881ae7"; // 2026-10-01: la tesorería única de Keellabs

/** Como LI.FI y Relay marcan el nativo de la cadena. */
export const NATIVO = "0x0000000000000000000000000000000000000000";
export const esNativo = (a: string) => a.toLowerCase() === NATIVO || /^0xe{6}/i.test(a);

// Los estables a los que tiene sentido barrer: la lista vive en `lib/estables.ts`, junto con la que
// usa `lib/precio.ts`. Estaban separadas y no decian lo mismo — el USDT de Arbitrum y HyperEVM se
// escribe `USD₮0`, con el simbolo, y esta lista tenia `USDT0` con T latina, asi que el barrido no lo
// ofrecia como destino. Ver la nota de ese fichero.
export { ESTABLES, esEstable } from "./estables";

export type CadenaPuente = {
  id: number;
  nombre: string;
  rpc: string;
  moneda: { name: string; symbol: string; decimals: number };
  explorador: string;
};

/**
 * Mas cadenas que las de Keellabs a proposito: de aqui viene el dinero antes de invertirlo.
 *
 * NO ES LA MISMA LISTA QUE `config/chains.ts`, y no deben igualarse: alli estan las redes donde
 * hay vaults —las que wagmi tiene que conocer para firmar— y aqui las de origen y destino del
 * puente, que son mas. Una cadena entra aqui en cuanto Relay o LI.FI la cotizan; entra alli solo
 * cuando hay una factory desplegada.
 */
export const CADENAS: CadenaPuente[] = [
  { id: 1, nombre: "Ethereum", rpc: "https://eth.llamarpc.com", moneda: { name: "Ether", symbol: "ETH", decimals: 18 }, explorador: "https://etherscan.io" },
  { id: 42161, nombre: "Arbitrum One", rpc: "https://arb1.arbitrum.io/rpc", moneda: { name: "Ether", symbol: "ETH", decimals: 18 }, explorador: "https://arbiscan.io" },
  { id: 8453, nombre: "Base", rpc: "https://mainnet.base.org", moneda: { name: "Ether", symbol: "ETH", decimals: 18 }, explorador: "https://basescan.org" },
  { id: 10, nombre: "OP Mainnet", rpc: "https://mainnet.optimism.io", moneda: { name: "Ether", symbol: "ETH", decimals: 18 }, explorador: "https://optimistic.etherscan.io" },
  // BNB Chain. Su nativo NO es ETH: si se copia la linea de al lado sin cambiar `moneda`, el
  // puente pide el gas en la moneda equivocada y las cuentas de "te alcanza para el gas" salen mal.
  // El RPC va por `/rpc/bsc/`, nuestro propio dominio, por lo mismo que en wagmi.ts: apuntando
  // al dominio de Binance las peticiones no salen del navegador en cuanto hay un bloqueador de
  // por medio, y aqui el sintoma seria una cartera vacia en vez de un error visible.
  { id: 56, nombre: "BNB Chain", rpc: "/rpc/bsc/", moneda: { name: "BNB", symbol: "BNB", decimals: 18 }, explorador: "https://bscscan.com" },
  { id: 4663, nombre: "Robinhood Chain", rpc: "https://rpc.mainnet.chain.robinhood.com", moneda: { name: "Ether", symbol: "ETH", decimals: 18 }, explorador: "https://robinhoodchain.blockscout.com" },
  { id: 999, nombre: "HyperEVM", rpc: "https://rpc.hyperliquid.xyz/evm", moneda: { name: "HYPE", symbol: "HYPE", decimals: 18 }, explorador: "https://hyperevmscan.io" },
];

export const cadena = (id: number) => CADENAS.find((c) => c.id === id);

export type TokenPuente = {
  address: Address;
  symbol: string;
  decimals: number;
  priceUSD?: string;
  /** saldo del usuario, en unidades humanas; solo en la lista de origen */
  saldo?: number;
  /**
   * El MISMO saldo en unidades crudas, tal cual lo devolvio la cadena. No es un duplicado por
   * comodidad: `saldo` es un `number` y a 18 decimales pierde los ultimos digitos, asi que
   * reconstruirlo con `parseUnits(String(saldo))` da una cifra MAYOR que la real y el puente pide
   * mas de lo que hay. Para «mandalo todo» hay que partir de este, no de aquel.
   */
  crudo?: string;
};

export type Paso = { id: string; to: Address; data: `0x${string}`; value: bigint; gas?: bigint };

export type Cotizacion = {
  fuente: "relay" | "lifi";
  ruta: string;
  /** cuanto llega, y el minimo garantizado */
  recibes: number;
  recibesMin: number;
  /** en unidades crudas del token de origen */
  desdeImporte: string;
  duracion: number | null;
  unidad: "s" | "min";
  comision: number;
  simboloComision: string;
  /** LI.FI entrega solo la tx del puente: la aprobacion la hacemos nosotros */
  direccionAprobacion?: Address;
  pasos: Paso[];
};

function mensajeApi(j: unknown): string {
  if (!j || typeof j !== "object") return "";
  const o = j as Record<string, unknown>;
  for (const k of ["message", "error", "detail", "description"]) {
    const v = o[k];
    if (typeof v === "string" && v) return v;
  }
  return "";
}

export async function cotizarRelay(
  cuenta: Address, deCadena: number, aCadena: number,
  deToken: string, aToken: string, importe: string
): Promise<Cotizacion> {
  const r = await fetch(`${RELAY}/quote`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      user: cuenta, recipient: cuenta,
      originChainId: deCadena, destinationChainId: aCadena,
      originCurrency: deToken, destinationCurrency: aToken,
      amount: importe, tradeType: "EXACT_INPUT",
      referrer: "keel.app", slippageTolerance: "50",
      appFees: [{ recipient: WALLET_COMISION, fee: String(COMISION_BPS) }],
    }),
  });
  const j = await r.json().catch(() => null);
  const pasos = (j?.steps ?? []) as Array<{ id: string; items?: Array<{ data?: Record<string, unknown> }> }>;
  if (!r.ok || !pasos.length) throw new Error(mensajeApi(j) || "sin ruta en Relay");

  const d = j.details ?? {};
  const salida = d.currencyOut ?? {};
  const fee = j.fees?.app ?? {};
  const recibes = Number(salida.amountFormatted ?? 0);
  const slip = parseFloat(d.slippageTolerance?.destination?.percent ?? "0") || 0;

  return {
    fuente: "relay", ruta: "Relay",
    recibes, recibesMin: recibes * (1 - slip / 100),
    desdeImporte: importe,
    duracion: d.timeEstimate ? Math.max(1, Math.round(d.timeEstimate)) : null,
    unidad: "s",
    comision: Number(fee.amountFormatted ?? 0),
    simboloComision: fee.currency?.symbol ?? "",
    pasos: pasos.flatMap((s) =>
      (s.items ?? []).map((it) => {
        const x = (it.data ?? {}) as Record<string, string>;
        return {
          id: s.id,
          to: x.to as Address,
          data: (x.data ?? "0x") as `0x${string}`,
          value: x.value ? BigInt(x.value) : 0n,
          gas: x.gas ? BigInt(x.gas) : undefined,
        };
      })
    ),
  };
}

export async function cotizarLifi(
  cuenta: Address, deCadena: number, aCadena: number,
  deToken: string, aToken: string, importe: string, decimalesDestino: number,
  decimalesOrigen = 18, simboloOrigen = ""
): Promise<Cotizacion> {
  // `fee` ES LA COMISION DE KEELLABS, y faltaba: sin este parametro LI.FI no cobra nada para el
  // integrador y toda ruta de respaldo salia GRATIS. No era un caso raro — Relay no soporta los
  // tokens de acciones de Robinhood (NVDA, GME: `UNSUPPORTED_CURRENCY`), asi que justo las
  // operaciones del producto caian aqui. Comprobado contra la API el 2026-08-15 con 2,8 NVDA:
  // sin `fee`, la comision total era 0,007 NVDA (0,25%, la de LI.FI); con `fee=0.001`, 0,0098
  // (0,35%) — la diferencia, 0,0028, es exactamente nuestro 0,10%.
  //
  // OJO: esto NO cae en `WALLET_COMISION` como el `appFees` de Relay. LI.FI lo acumula en su
  // colector a nombre del integrador y hay que reclamarlo desde su panel.
  const u = new URLSearchParams({
    fromChain: String(deCadena), toChain: String(aCadena),
    fromToken: deToken, toToken: aToken, fromAmount: importe,
    fromAddress: cuenta, integrator: "keel-app", slippage: "0.005", order: "CHEAPEST",
    fee: String(COMISION_BPS / 10000),
  });
  const q = await fetch(`${LIFI}/quote?${u}`).then((r) => r.json()).catch(() => null);
  if (!q?.transactionRequest) throw new Error(mensajeApi(q) || "sin ruta en LI.FI");

  const est = q.estimate;
  const tr = q.transactionRequest;
  const humano = (raw: string) => Number(raw) / 10 ** decimalesDestino;

  return {
    fuente: "lifi",
    ruta: q.toolDetails?.name ?? q.tool ?? "LI.FI",
    recibes: humano(est.toAmount),
    recibesMin: humano(est.toAmountMin),
    desdeImporte: est.fromAmount,
    duracion: est.executionDuration ? Math.round(est.executionDuration / 60) : null,
    unidad: "min",
    // La nuestra va SUMADA dentro del «LIFI Fixed Fee» de `feeCosts`, sin linea propia, asi que no
    // se puede leer de la respuesta: se calcula sobre el importe de origen, que es como la aplica.
    comision: (Number(est.fromAmount) / 10 ** decimalesOrigen) * (COMISION_BPS / 10000),
    simboloComision: simboloOrigen,
    direccionAprobacion: est.approvalAddress as Address,
    pasos: [{
      id: "bridge", to: tr.to as Address, data: tr.data as `0x${string}`,
      value: tr.value ? BigInt(tr.value) : 0n,
      gas: tr.gasLimit ? BigInt(tr.gasLimit) : undefined,
    }],
  };
}

/**
 * Relay primero, LI.FI de respaldo, y si los dos fallan se enseñan LOS DOS motivos. Es el mismo
 * orden que usaba la pantalla del puente a mano; vive aqui porque el barrido cotiza decenas de
 * monedas y dos copias de esta cascada acabarian cobrando comisiones distintas.
 */
export async function cotizarMejor(
  cuenta: Address, deCadena: number, aCadena: number,
  de: TokenPuente, a: TokenPuente, importe: string
): Promise<Cotizacion> {
  let porQue = "";
  try {
    return await cotizarRelay(cuenta, deCadena, aCadena, de.address, a.address, importe);
  } catch (e) { porQue = e instanceof Error ? e.message : String(e); }
  try {
    return await cotizarLifi(
      cuenta, deCadena, aCadena, de.address, a.address, importe,
      a.decimals ?? 18, de.decimals ?? 18, de.symbol ?? ""
    );
  } catch (e2) {
    throw new Error(`${porQue} · ${e2 instanceof Error ? e2.message : e2}`);
  }
}
